import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextControl,
  Form,
  SelectField,
  FormItem,
  IconButton,
  Chip,
  Alert,
} from "@tiny/components";
import { useManageCustomAttributesDialog } from "./ManageCustomAttributesDialog.hooks";
import TrashFull from "@tiny/icons/generated/TrashFull";
import { CustomAttributeDefinitionView } from "@tiny/api";
import { useStyles } from "./ManageCustomAttributesDialog.styles";

export interface ManageCustomAttributesDialogProps {
  open: boolean;
  onClose: () => void;
}

const DATA_TYPE_OPTIONS = [
  { label: "Text", value: "STRING" },
  { label: "Number", value: "INTEGER" },
  { label: "True / False", value: "BOOLEAN" },
  { label: "Date", value: "DATE" },
  { label: "Array", value: "ARRAY" },
];

const DATA_TYPE_DESCRIPTION: Record<string, string> = {
  STRING: "Any text value, e.g. a name or code",
  INTEGER: "A whole number, e.g. order count or score",
  BOOLEAN: "A true/false flag, e.g. isVip or hasOptedIn",
  DATE: "A calendar date, e.g. birthday or signup date",
  ARRAY: "A list of values, e.g. interests, products owned, or subscriptions",
};

const DATA_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  DATA_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

// Theme pastels are too light for white text — use explicit bg+text combos
const TYPE_CHIP_STYLE: Record<string, React.CSSProperties> = {
  STRING: { backgroundColor: "#41B8D0", color: "#fff" }, // blue.dark
  INTEGER: { backgroundColor: "#008060", color: "#fff" }, // primary.main
  BOOLEAN: { backgroundColor: "#734FF7", color: "#fff" }, // purple.dark
  DATE: { backgroundColor: "#EAC263", color: "#3F4451" }, // yellow.dark + dark text
  ARRAY: { backgroundColor: "#E8833A", color: "#fff" }, // orange.dark
};

function ManageCustomAttributesDialog(
  props: ManageCustomAttributesDialogProps
) {
  const { open, onClose } = props;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
    >
      <ManageCustomAttributesDialogContent {...props} />
    </Dialog>
  );
}

function ManageCustomAttributesDialogContent(
  props: ManageCustomAttributesDialogProps
) {
  const { onClose } = props;
  const classes = useStyles();
  const {
    attributes,
    isLoading,
    error,
    form,
    dataType,
    setDataType,
    handleAdd,
    handleDelete,
    isAdding,
    isDeletingId,
  } = useManageCustomAttributesDialog();

  return (
    <>
      <DialogTitle
        subtitle="Define custom fields to capture extra data about your contacts."
        onClose={onClose}
      >
        Manage Custom Attributes
      </DialogTitle>
      <DialogContent>
        {/* Add new attribute section */}
        <Box className={classes.addSection}>
          <Box mb={2} className={classes.sectionHeader}>
            <Typography
              variant="body2"
              color="grey"
              tone={700}
              fontWeight="semiBold"
            >
              Add new attribute
            </Typography>
          </Box>
          <Form form={form as never} onSubmit={handleAdd as never}>
            <TextControl
              name="attributeName"
              label="Name"
              defaultValue=""
              required
              maxLength={64}
              placeholder="e.g. orderId, score, isVip"
              validate={(v: string) =>
                /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/.test(v) ||
                "Only letters, numbers, and single spaces between words are allowed"
              }
            />
            <Box mt={4}>
              <FormItem
                name="dataType"
                defaultValue="STRING"
                rules={{
                  required: { value: true, message: "Type is required" },
                }}
              >
                <SelectField
                  label="Type"
                  options={DATA_TYPE_OPTIONS}
                  value={dataType}
                  onChange={(_e: any, value: any) =>
                    setDataType(value?.value ?? value)
                  }
                />
              </FormItem>
            </Box>
            {DATA_TYPE_DESCRIPTION[dataType] && (
              <Box mt={1}>
                <Typography variant="body3" color="primary" tone={"main"}>
                  {DATA_TYPE_DESCRIPTION[dataType]}
                </Typography>
              </Box>
            )}
            <Box mt={2}>
              <TextControl
                name="defaultValue"
                label="Default value (optional)"
                defaultValue=""
                maxLength={256}
                placeholder={
                  dataType === "ARRAY"
                    ? "e.g. value1, value2, value3"
                    : "Leave blank for no default"
                }
                validate={(v: string) => {
                  if (!v) {
                    return true;
                  }
                  if (dataType === "ARRAY") {
                    return (
                      /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*(,\s*[a-zA-Z0-9]+( [a-zA-Z0-9]+)*)*$/.test(
                        v
                      ) ||
                      "Separate values with commas (,). Only letters, numbers, and single spaces are allowed."
                    );
                  }
                  return (
                    /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/.test(v) ||
                    "Only letters, numbers, and single spaces between words are allowed"
                  );
                }}
              />
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={handleAdd}
                loading={isAdding}
                disabled={isAdding}
              >
                Add
              </Button>
            </Box>
          </Form>
        </Box>

        <Divider mt={4} mb={4} />

        {/* Existing attributes section */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography
            variant="body2"
            color="grey"
            tone={700}
            fontWeight="semiBold"
          >
            Existing attributes
          </Typography>
          {!isLoading && attributes.length > 0 && (
            <Typography variant="body2" color="grey" tone={700}>
              {attributes.length}{" "}
              {attributes.length === 1 ? "attribute" : "attributes"}
            </Typography>
          )}
        </Box>

        {error && (
          <Box mb={3}>
            <Alert severity="error" size="small" variant="filled">
              Failed to load custom attributes. Please try again.
            </Alert>
          </Box>
        )}

        {isLoading ? (
          <Box py={4}>
            <Typography variant="body2" color="grey" tone={400}>
              Loading...
            </Typography>
          </Box>
        ) : attributes.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" color="grey" tone={400}>
              No custom attributes yet.
            </Typography>
            <Box mt={1}>
              <Typography variant="body2" color="grey" tone={400}>
                Use the form above to add your first one.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {attributes.map(
              (attr: CustomAttributeDefinitionView, index: number) => (
                <Box key={attr.id} mb={index < attributes.length - 1 ? 1 : 0}>
                  <Box className={classes.attributeRow}>
                    <Box className={classes.attributeRowLeft}>
                      <Box mr={2} style={{ flexShrink: 0 }}>
                        <Chip
                          label={
                            DATA_TYPE_LABEL[attr.dataType] ?? attr.dataType
                          }
                          size="small"
                          style={TYPE_CHIP_STYLE[attr.dataType] ?? {}}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="grey"
                        tone={800}
                        fontWeight="semiBold"
                        noWrap
                      >
                        {attr.attributeName}
                      </Typography>
                    </Box>
                    <Box className={classes.attributeRowMid}>
                      {attr.defaultValue ? (
                        <Box className={classes.defaultValuePill}>
                          <Typography
                            variant="body3"
                            color="grey"
                            tone={500}
                            noWrap
                          >
                            Default:&nbsp;
                          </Typography>
                          <Typography
                            variant="body3"
                            color="grey"
                            tone={800}
                            fontWeight="semiBold"
                            noWrap
                          >
                            {Array.isArray(attr.defaultValue)
                              ? attr.defaultValue.join(", ")
                              : attr.defaultValue}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body3" color="grey" tone={600}>
                          No default
                        </Typography>
                      )}
                    </Box>
                    <Box
                      className={classes.deleteBtn}
                      style={{ flexShrink: 0 }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(attr.id)}
                        disabled={isDeletingId === attr.id}
                        style={{ color: "#D32F2F" }}
                      >
                        <TrashFull />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              )
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </>
  );
}

export default ManageCustomAttributesDialog;
